<script>
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { fly, fade } from "svelte/transition";
  import { tourFunctions } from "../tour-functions";
  import { focusTrap } from "./utils.js";
  import CloseButton from "./CloseButton.svelte";
  import UAParser from "ua-parser-js";

  let isMobile = new UAParser().getResult().device["type"] == "mobile";

  export let ariaLabel = "Popup";
  export let visible = true;
  export let width = null;
  export let textColor = null;
  export let bgColor = null;

  let popupModal;
  let scrollContainer;
  const dispatch = createEventDispatcher();

  // Dispatch events on open and close
  $: visible ? dispatch("open") : dispatch("close");
  $: if (visible) focusPopup();
  // Create a focus trap
  async function focusPopup() {
    await tick();
    scrollContainer.focus();
  }

  function closePopup() {
    visible = false;
    tourFunctions.focus();
  }

  function getPopupTextColor() {
    if (textColor) return textColor;
    // get --popup-text-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupTextColor = rootStyles.getPropertyValue("--popup-text-color");
    return popupTextColor || "#000";
  }

  function getPopupBgColor() {
    if (bgColor) return bgColor;
    // get --popup-bg-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupBgColor = rootStyles.getPropertyValue("--popup-bg-color");
    return popupBgColor || "#fff";
  }

  /** Panel width only — close button sits outside, like PopupHotspot. */
  function getDesktopContentWidth(value) {
    if (!value || isMobile) return null;
    const normalized = String(value).trim();
    if (normalized.endsWith("%")) {
      return normalized.replace(/%$/, "vw");
    }
    return normalized;
  }

  $: desktopContentWidth = getDesktopContentWidth(width);
</script>

{#if visible}
  <div id="popup-gallery"></div>

  <div bind:this={popupModal} class="popup-modal" use:focusTrap>
    <div class="popup-area">
      <div class="popup-window-wrap">
        <div class="popup-window" transition:fly={{ y: 800, duration: 500 }}>
          <div class="popup-layout">
            <div
              class="popup-content"
              style={`
              ${desktopContentWidth ? `width: ${desktopContentWidth};` : ""}
              --popup-text-color: ${getPopupTextColor()};
              --popup-bg-color: ${getPopupBgColor()};
            `}
            >
              <div class="popup">
                <div class="content column">
                  <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                  <div
                    class="content-inner"
                    role="dialog"
                    aria-label={ariaLabel}
                    tabindex="0"
                    bind:this={scrollContainer}
                  >
                    <slot {scrollContainer} />
                  </div>
                </div>
              </div>
            </div>
            <div class="close-button-container">
              <CloseButton
                close={closePopup}
                color="#ffffff"
                useAbsolutePosition={false}
                marginBottom={isMobile ? "0" : "1rem"}
              />
            </div>
          </div>
        </div>
      </div>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="popup-bg"
        on:click={closePopup}
        transition:fade={{ duration: 500 }}
      ></div>
    </div>
  </div>
{/if}

<style>
  .popup {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  /* :global(.popup-content *:focus) {
    outline: 2px dotted var(--popup-text-color);
  } */

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    height: 100%;
    min-width: 0; /* Allow flex item to shrink below content size */
  }

  .content {
    justify-content: center;
    font-family: var(--font-body), sans-serif;
    line-height: 1.5;
  }

  .content-inner {
    padding: 2rem;
    padding-top: 0;
    overflow-y: scroll;
    max-height: 90vh;
    box-sizing: border-box;
    width: 100%;
    min-width: 0; /* Allow content to shrink */
  }

  .content-inner:focus {
    outline: none;
  }

  :global(.popup .content-inner a) {
    color: var(--popup-text-color);
    font-weight: 700;
  }

  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--popup-text-color);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--popup-text-color);
  }

  .popup-area {
    position: fixed;
    z-index: 10001;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
  }

  .popup-bg {
    position: fixed;
    z-index: 10000;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.66);
    backdrop-filter: blur(2px);
  }

  .popup-window-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    width: fit-content;
    max-width: calc(100vw - 4rem);
    margin: 2rem auto;
    max-height: calc(100vh - 4rem);
    box-sizing: border-box;
  }

  .popup-layout {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 95vh;
    max-width: 2000px;
    margin: auto;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .popup-window {
    position: relative;
    z-index: 99999;
    background: transparent;
    width: auto;
    max-width: 95vw;
    max-height: 95vh;
    margin: 0;
    overflow: visible;
  }

  .popup-content {
    flex: 0 1 auto;
    border-radius: 10px;
    background: var(--popup-bg-color);
    color: var(--popup-text-color);
    overflow: hidden;
    width: 40rem;
    max-width: calc(100vw - 4rem - 50px - 1rem);
    max-height: 95vh;
    padding: 0;
  }

  @media only screen and (max-width: 600px) {
    .popup-area {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      justify-content: center;
      align-items: center;
      overflow: hidden;
    }

    .popup-window-wrap {
      width: calc(100vw - 2rem) !important;
      max-width: calc(100vw - 2rem);
      max-height: calc(100dvh - 2rem);
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-window {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 100%;
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup {
      position: relative;
      height: auto;
    }

    .column {
      width: 100%;
      flex: none;
    }

    .image.column {
      min-height: 40%;
      height: fit-content;
      aspect-ratio: 1/1;
    }

    .popup-layout {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      max-height: calc(100dvh - 2rem);
      min-height: 0;
      flex: 1 1 auto;
      width: 100%;
    }

    .close-button-container {
      flex-shrink: 0;
      align-self: center;
    }

    .popup {
      height: auto;
      flex-direction: column;
    }

    .popup-content {
      width: 100%;
      max-width: 100%;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .content {
      justify-content: flex-start;
      font-family: var(--font-body), sans-serif;
      line-height: 1.5;
    }

    .content-inner {
      font-size: 14px;
      height: auto;
      max-height: none;
      overflow-y: visible;
      padding: 1.25rem;
      padding-top: 0;
    }
  }
</style>
